import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';

import Assignee from 'models/assignee';
import connect from 'components/lib/connect';
import ConversationItem from 'models/conversation_item';
import ConversationItemMenu from 'components/customer/conversation_history/conversation_items_v2/menu/conversation_item_menu_container';
import ConversationItemType from 'models/conversation_item_type';
import { createConversationItemFromItemLink } from 'models/item_link_content';
import { getIsCommentingEnabledForTask } from 'scripts/application/selectors/customer';
import Linkifier from 'components/lib/linkifier';
import LongDateTime from 'components/lib/date/long_date_time';
import SafeHtml from 'components/common/safe_html';
import TaskAnalyticsLocations from 'actions/conversation_item/task/task_analytics_locations';
import TaskComment from './task_comment';
import TaskCommentEditor from './task_comment_editor';
import TaskHeader from 'components/customer/conversation_history/conversation_items_v2/task/task_header';
import TrackCommentPanelVisibility from 'actions/task/track_comment_panel_visibility';
import { typeReflect } from 'models/conversation_item/content_from_js';
import UpdateTaskAssignee from 'actions/conversation_item/task/update_task_assignee';
import { useExecuteAction } from 'components/hooks/connect_hooks';
import { useOnUnmount } from 'components/hooks/lifecycle_hooks';

export function TaskCommentPanel({ comments = [], isCommentingEnabled, item, lastOpenedAt }) {
  const executeAction = useExecuteAction();

  const itemId = item && item.id;
  const onChangeAssignee = useCallback(
    ({ agentId, routingGroupId }) =>
      executeAction(UpdateTaskAssignee, {
        assignee: new Assignee({ agentId: agentId || '', routingGroupId }),
        conversationItemId: itemId,
        location: TaskAnalyticsLocations.SIDE_PANEL,
      }),
    [itemId]
  );

  let scrollBottomRef = useRef(null);
  useEffect(() => scrollBottomRef.current && scrollBottomRef.current.scrollIntoView({ behavior: 'smooth' }), [
    comments.length,
  ]);

  // we want to log the comment panel as OPEN every time it becomes visible
  // or displays a different task (this can happen while it is already open)
  // BUT we only want to log the panel as CLOSED when it closes completely and
  // NOT when it refreshes to show a new task
  let itemIdRef = useRef(itemId);
  useEffect(() => {
    executeAction(TrackCommentPanelVisibility, { itemId, isVisible: true });
    itemIdRef.current = itemId;
  }, [itemId]);
  useOnUnmount(() => executeAction(TrackCommentPanelVisibility, { itemId: itemIdRef.current, isVisible: false }), []);

  if (!item) {
    return null;
  }
  const isLink = item.isLink();
  const task = item.content;

  function renderTaskDetail() {
    return (
      <StyledDetail>
        <TaskHeader
          isLink={isLink}
          location={TaskAnalyticsLocations.SIDE_PANEL}
          onChangeAssignee={onChangeAssignee}
          item={item}
        />
        <StyledItemMenu item={item} hideComment location={TaskAnalyticsLocations.SIDE_PANEL} />
        <StyledDueDate>
          Due
          <LongDateTime className="taskItem-dueDate-timestamp" timestamp={moment(task.dueAt)} />
        </StyledDueDate>
      </StyledDetail>
    );
  }

  function renderTaskMessage() {
    const { bodyHtml, note } = task;

    if (bodyHtml) {
      return <SafeHtml data-aid="taskCommentPanel-taskMessage" html={bodyHtml} />;
    } else if (note) {
      return <Linkifier data-aid="taskCommentPanel-taskMessage">{note}</Linkifier>;
    }

    return null;
  }

  function renderComments() {
    return (
      <div>
        {comments.map(c => (
          <TaskComment {...c} key={c.id} />
        ))}
      </div>
    );
  }

  function renderEditor() {
    return isCommentingEnabled && <TaskCommentEditor itemId={itemId} lastOpenedAt={lastOpenedAt} />;
  }

  return (
    <Panel data-aid="taskCommentPanel" tabIndex="0">
      {renderTaskDetail()}
      <StyledTaskMessages>
        {renderTaskMessage()}
        {renderComments()}
        {renderEditor()}
        <div ref={scrollBottomRef} />
      </StyledTaskMessages>
    </Panel>
  );
}

const Panel = styled.div`
  box-shadow: ${p => p.theme.boxShadow.small};
  display: flex;
  flex: 0 0 25%;
  flex-direction: column;
  max-width: 360px;
  min-width: 200px;
  outline: none;
  width: 25%;
  z-index: 3;
`;

TaskCommentPanel.propTypes = {
  comments: PropTypes.array,
  item: PropTypes.instanceOf(ConversationItem).isRequired,
};

const StyledItemMenu = styled(ConversationItemMenu)`
  .conversationItemMenu-container {
    top: 25px;
    .conversationItemMenu-button {
      padding-top: 0px;
    }
  }
`;

const StyledDetail = styled.div`
  background: ${p => p.theme.colors.gray100};
  padding: 20px 16px 16px;
`;

const StyledDueDate = styled.div`
  font-weight: 600;
`;

const StyledTaskMessages = styled.div`
  overflow: auto;
  padding: 16px 16px 0px;
`;

function mapStateToProps({ getProvider, isFeatureEnabled }) {
  const commentPanelProvider = getProvider('commentPanel');
  const historyProvider = getProvider('conversationHistory');
  const taskInfoProvider = getProvider('taskInfo');

  const panel = commentPanelProvider.get();
  const selectedItemId = panel.selectedItemId;
  const lastOpenedAt = panel.lastOpenedAt;

  let item = historyProvider.findBy({ id: selectedItemId });
  item = item && historyProvider.isPending(selectedItemId) ? historyProvider.getPending(selectedItemId) : item;
  if (!item) return {};

  if (typeReflect.instanceToType(item.content) === ConversationItemType.ITEM_LINK) {
    item = createConversationItemFromItemLink(item);
  }

  let taskInfo = taskInfoProvider.findBy({ id: item.id });
  let comments = taskInfo ? taskInfo.getComments() : [];

  return {
    comments,
    isCommentingEnabled: getIsCommentingEnabledForTask(
      { taskInfo: taskInfoProvider, conversationHistory: historyProvider },
      { taskId: item.id }
    ),
    item,
    lastOpenedAt,
  };
}

const TaskCommentPanelContainer = connect(mapStateToProps)(TaskCommentPanel);
export default TaskCommentPanelContainer;



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/customer/task_comments/task_comment_panel.jsx